import { API_URL } from '@/config';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Link2, Image, Save, Eye, RefreshCw, Upload, Palette, Type, Layout, UserCheck, Phone, Mail, User, Shield, Trash2, Plus, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import getImageUrl from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const VoteLinkDesigner = () => {
  const { user } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [nominees, setNominees] = useState([]);
  const [selectedNominees, setSelectedNominees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, banner: false });
  
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  // Design settings
  const [design, setDesign] = useState({
    // Header
    header_title: 'Vote for Your Favorite Nominee',
    header_subtitle: 'Your vote matters! Support the nominee by casting your vote.',
    show_event_banner: true,
    
    // Colors
    primary_color: '#D4AF37',
    secondary_color: '#0F172A',
    background_gradient_start: '#0F172A',
    background_gradient_end: '#1E293B',
    card_bg_color: '#FFFFFF',
    text_color: '#0F172A',
    
    // Nominee Card
    show_nominee_photo: true,
    nominee_photo_size: 'large',
    show_category: true,
    show_vote_count: true,
    show_description: true,
    
    // Button
    vote_button_text: 'Vote Now',
    vote_button_color: '#D4AF37',
    
    // Footer
    footer_text: 'Thank you for participating!',
    show_share_button: true,
    
    // Custom Images
    logo_url: '',
    banner_url: '',
    
    // Voter Fields Configuration
    voter_fields: [
      { id: 'name', label: 'Full Name', type: 'text', required: true, enabled: true, icon: 'user' },
      { id: 'email', label: 'Email Address', type: 'email', required: true, enabled: true, icon: 'mail' },
      { id: 'phone', label: 'Phone Number', type: 'tel', required: false, enabled: true, icon: 'phone' },
    ],
    
    // OTP Verification
    enable_otp_verification: false,
    otp_field: 'both', // 'both', 'phone' or 'email'
    otp_message: 'Your OTP for voting is: {OTP}. Valid for 5 minutes.',
  });

  useEffect(() => {
    loadEvents();
    loadSavedDesign();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadNominees();
    }
  }, [selectedEvent]);

  const loadEvents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pollingEvents = response.data.filter(e => e.polling_required);
      setEvents(pollingEvents);
      if (pollingEvents.length > 0) {
        setSelectedEvent(pollingEvents[0].event_id);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNominees = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/votes/admin/event/${selectedEvent}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominees(response.data.nominees || []);
    } catch (error) {
      console.error('Failed to load nominees:', error);
    }
  };

  const loadSavedDesign = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/settings/vote-link-design`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDesign(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Design not saved yet
    }
  };

  const handleSaveDesign = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/settings/vote-link-design`, design, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design saved successfully!');
    } catch (error) {
      toast.error('Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const url = response.data.url;
      setDesign(prev => ({ 
        ...prev, 
        [type === 'logo' ? 'logo_url' : 'banner_url']: url 
      }));
      toast.success(`${type === 'logo' ? 'Logo' : 'Banner'} uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${type}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSelectAll = () => {
    if (selectedNominees.length === nominees.length) {
      setSelectedNominees([]);
    } else {
      setSelectedNominees(nominees.map(n => n.submission_id));
    }
  };

  const toggleNomineeSelection = (submissionId) => {
    setSelectedNominees(prev => 
      prev.includes(submissionId) 
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  // Voter fields management
  const updateVoterField = (index, field, value) => {
    setDesign(prev => ({
      ...prev,
      voter_fields: prev.voter_fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const addVoterField = () => {
    const newField = {
      id: `custom_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      enabled: true,
      icon: 'user'
    };
    setDesign(prev => ({
      ...prev,
      voter_fields: [...prev.voter_fields, newField]
    }));
  };

  const removeVoterField = (index) => {
    setDesign(prev => ({
      ...prev,
      voter_fields: prev.voter_fields.filter((_, i) => i !== index)
    }));
  };

  const photoSizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const fieldIcons = {
    user: User,
    mail: Mail,
    phone: Phone
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vote-link-designer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Vote Link Designer</h1>
          <p className="text-sm text-muted-foreground">Customize voting pages with OTP verification</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSaveDesign} disabled={saving} style={{ backgroundColor: '#D4AF37' }}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Design
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Design Settings */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="content" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="colors">Colors</TabsTrigger>
              <TabsTrigger value="layout">Layout</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="voter">Voter Fields</TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Type className="w-5 h-5" />
                    Text Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Header Title</Label>
                    <Input
                      value={design.header_title}
                      onChange={(e) => setDesign(prev => ({ ...prev, header_title: e.target.value }))}
                      placeholder="Vote for Your Favorite Nominee"
                    />
                  </div>
                  <div>
                    <Label>Header Subtitle</Label>
                    <Textarea
                      value={design.header_subtitle}
                      onChange={(e) => setDesign(prev => ({ ...prev, header_subtitle: e.target.value }))}
                      placeholder="Your vote matters!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Vote Button Text</Label>
                    <Input
                      value={design.vote_button_text}
                      onChange={(e) => setDesign(prev => ({ ...prev, vote_button_text: e.target.value }))}
                      placeholder="Vote Now"
                    />
                  </div>
                  <div>
                    <Label>Footer Text</Label>
                    <Input
                      value={design.footer_text}
                      onChange={(e) => setDesign(prev => ({ ...prev, footer_text: e.target.value }))}
                      placeholder="Thank you for participating!"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Colors Tab */}
            <TabsContent value="colors" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Color Scheme
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.primary_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.primary_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.secondary_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, secondary_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.secondary_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, secondary_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Background Gradient Start</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.background_gradient_start}
                          onChange={(e) => setDesign(prev => ({ ...prev, background_gradient_start: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.background_gradient_start}
                          onChange={(e) => setDesign(prev => ({ ...prev, background_gradient_start: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Background Gradient End</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.background_gradient_end}
                          onChange={(e) => setDesign(prev => ({ ...prev, background_gradient_end: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.background_gradient_end}
                          onChange={(e) => setDesign(prev => ({ ...prev, background_gradient_end: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Vote Button Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.vote_button_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, vote_button_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.vote_button_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, vote_button_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Card Background</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={design.card_bg_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, card_bg_color: e.target.value }))}
                          className="w-12 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={design.card_bg_color}
                          onChange={(e) => setDesign(prev => ({ ...prev, card_bg_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Layout Tab */}
            <TabsContent value="layout" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Layout className="w-5 h-5" />
                    Layout Options
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Event Banner</Label>
                      <p className="text-xs text-muted-foreground">Display the event banner at the top</p>
                    </div>
                    <Switch
                      checked={design.show_event_banner}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_event_banner: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Nominee Photo</Label>
                      <p className="text-xs text-muted-foreground">Display the nominee's photo</p>
                    </div>
                    <Switch
                      checked={design.show_nominee_photo}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_nominee_photo: checked }))}
                    />
                  </div>
                  <div>
                    <Label>Nominee Photo Size</Label>
                    <Select
                      value={design.nominee_photo_size}
                      onValueChange={(value) => setDesign(prev => ({ ...prev, nominee_photo_size: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small (64x64 px)</SelectItem>
                        <SelectItem value="medium">Medium (96x96 px)</SelectItem>
                        <SelectItem value="large">Large (128x128 px)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Category</Label>
                      <p className="text-xs text-muted-foreground">Display nominee's award category</p>
                    </div>
                    <Switch
                      checked={design.show_category}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_category: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Vote Count</Label>
                      <p className="text-xs text-muted-foreground">Display current vote count</p>
                    </div>
                    <Switch
                      checked={design.show_vote_count}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_vote_count: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Description</Label>
                      <p className="text-xs text-muted-foreground">Display nominee's description</p>
                    </div>
                    <Switch
                      checked={design.show_description}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_description: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Share Button</Label>
                      <p className="text-xs text-muted-foreground">Allow voters to share the link</p>
                    </div>
                    <Switch
                      checked={design.show_share_button}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, show_share_button: checked }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Images Tab */}
            <TabsContent value="images" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Custom Images
                  </CardTitle>
                  <CardDescription>
                    Upload images or paste URLs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Logo */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-base font-semibold">Logo</Label>
                      <p className="text-xs text-muted-foreground">Recommended: 200x80 pixels (PNG with transparent background)</p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={design.logo_url}
                        onChange={(e) => setDesign(prev => ({ ...prev, logo_url: e.target.value }))}
                        placeholder="Paste URL or upload below"
                        className="flex-1"
                      />
                      <input
                        type="file"
                        ref={logoInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('logo', e.target.files?.[0])}
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploading.logo}
                      >
                        {uploading.logo ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    {design.logo_url && (
                      <div className="p-3 bg-gray-100 rounded-lg inline-block">
                        <img 
                          src={getImageUrl(design.logo_url)} 
                          alt="Logo preview" 
                          className="h-12 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Banner */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-base font-semibold">Banner</Label>
                      <p className="text-xs text-muted-foreground">Recommended: 1200x400 pixels (JPG/PNG)</p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={design.banner_url}
                        onChange={(e) => setDesign(prev => ({ ...prev, banner_url: e.target.value }))}
                        placeholder="Paste URL or upload below"
                        className="flex-1"
                      />
                      <input
                        type="file"
                        ref={bannerInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload('banner', e.target.files?.[0])}
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => bannerInputRef.current?.click()}
                        disabled={uploading.banner}
                      >
                        {uploading.banner ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    {design.banner_url && (
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <img 
                          src={getImageUrl(design.banner_url)} 
                          alt="Banner preview" 
                          className="w-full max-h-32 object-cover rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Voter Fields Tab */}
            <TabsContent value="voter" className="space-y-4">
              {/* OTP Verification */}
              <Card className="border-2 border-green-200">
                <CardHeader className="bg-green-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    OTP Verification (Twilio)
                  </CardTitle>
                  <CardDescription>
                    Verify voters via SMS OTP before accepting their vote
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable OTP Verification</Label>
                      <p className="text-xs text-muted-foreground">Send OTP to verify voter authenticity</p>
                    </div>
                    <Switch
                      checked={design.enable_otp_verification}
                      onCheckedChange={(checked) => setDesign(prev => ({ ...prev, enable_otp_verification: checked }))}
                    />
                  </div>
                  
                  {design.enable_otp_verification && (
                    <>
                      <div>
                        <Label>Verification Method</Label>
                        <Select
                          value={design.otp_field}
                          onValueChange={(value) => setDesign(prev => ({ ...prev, otp_field: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Both Phone & Email</SelectItem>
                            <SelectItem value="phone">Phone (SMS via Twilio)</SelectItem>
                            <SelectItem value="email">Email Only</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          {design.otp_field === 'both' 
                            ? 'OTP will be sent to both phone and email. If one is blank, it sends to the available channel.'
                            : `OTP will be sent only via ${design.otp_field === 'phone' ? 'SMS' : 'Email'}`
                          }
                        </p>
                      </div>
                      <div>
                        <Label>OTP Message Template</Label>
                        <p className="text-xs text-muted-foreground mb-2">Use {'{OTP}'} as placeholder for the code</p>
                        <Textarea
                          value={design.otp_message}
                          onChange={(e) => setDesign(prev => ({ ...prev, otp_message: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <Alert className="border-amber-200 bg-amber-50">
                        <Phone className="w-4 h-4 text-amber-600" />
                        <AlertDescription className="text-amber-800">
                          <strong>Note:</strong> When OTP is enabled, {design.otp_field === 'both' 
                            ? 'either Phone or Email is required (at least one must be provided).' 
                            : `the ${design.otp_field === 'phone' ? 'Phone Number' : 'Email'} field will be automatically set as required.`
                          }
                        </AlertDescription>
                      </Alert>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Voter Fields */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserCheck className="w-5 h-5" />
                    Voter Information Fields
                  </CardTitle>
                  <CardDescription>
                    Configure which information to collect from voters
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {design.voter_fields.map((field, index) => (
                    <div 
                      key={field.id} 
                      className={`p-4 rounded-lg border ${field.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex items-center gap-2 pt-1">
                          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                          <Checkbox
                            checked={field.enabled}
                            onCheckedChange={(checked) => updateVoterField(index, 'enabled', checked)}
                          />
                        </div>
                        
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Field Label</Label>
                            <Input
                              value={field.label}
                              onChange={(e) => updateVoterField(index, 'label', e.target.value)}
                              disabled={!field.enabled}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Field Type</Label>
                            <Select
                              value={field.type}
                              onValueChange={(value) => updateVoterField(index, 'type', value)}
                              disabled={!field.enabled}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="tel">Phone</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Icon</Label>
                            <Select
                              value={field.icon}
                              onValueChange={(value) => updateVoterField(index, 'icon', value)}
                              disabled={!field.enabled}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="mail">Email</SelectItem>
                                <SelectItem value="phone">Phone</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 pt-5">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`required-${index}`}
                              checked={field.required}
                              onCheckedChange={(checked) => updateVoterField(index, 'required', checked)}
                              disabled={!field.enabled || (design.enable_otp_verification && field.type === design.otp_field)}
                            />
                            <Label htmlFor={`required-${index}`} className="text-xs font-normal">
                              Required
                            </Label>
                          </div>
                          
                          {!['name', 'email', 'phone'].includes(field.id) && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeVoterField(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button variant="outline" onClick={addVoterField} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Field
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Nominee Selection & Preview */}
        <div className="space-y-6">
          {/* Event Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Apply To</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Event</Label>
                <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.event_id} value={event.event_id}>
                        {event.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {nominees.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Nominees</Label>
                    <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                      {selectedNominees.length === nominees.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
                    {nominees.slice(0, 10).map((nominee) => (
                      <div
                        key={nominee.submission_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedNominees.includes(nominee.submission_id) ? 'bg-amber-50 border border-amber-200' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleNomineeSelection(nominee.submission_id)}
                      >
                        <Checkbox checked={selectedNominees.includes(nominee.submission_id)} onChange={() => {}} />
                        <span className="text-sm truncate flex-1">{nominee.nominee_name}</span>
                      </div>
                    ))}
                    {nominees.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        +{nominees.length - 10} more nominees
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedNominees.length === 0 ? 'Design applies to ALL nominees' : `Selected: ${selectedNominees.length}`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mini Preview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="rounded-lg overflow-hidden"
                style={{ 
                  background: `linear-gradient(135deg, ${design.background_gradient_start} 0%, ${design.background_gradient_end} 100%)`,
                  padding: '0.75rem'
                }}
              >
                <div 
                  className="rounded-lg p-3 text-center space-y-2"
                  style={{ backgroundColor: design.card_bg_color }}
                >
                  {design.show_nominee_photo && (
                    <div 
                      className={`${photoSizeClasses[design.nominee_photo_size]} mx-auto rounded-full bg-gray-200 flex items-center justify-center`}
                      style={{ border: `3px solid ${design.primary_color}` }}
                    >
                      <span className="text-[8px] text-gray-400">Photo</span>
                    </div>
                  )}
                  <h3 className="font-bold text-xs" style={{ color: design.text_color }}>Nominee Name</h3>
                  {design.show_category && (
                    <p className="text-[10px]" style={{ color: design.primary_color }}>Category</p>
                  )}
                  
                  {/* Voter Fields Preview */}
                  <div className="space-y-1 text-left px-2">
                    {design.voter_fields.filter(f => f.enabled).map((field) => {
                      const IconComponent = fieldIcons[field.icon] || User;
                      return (
                        <div key={field.id} className="flex items-center gap-1 text-[9px] text-gray-500">
                          <IconComponent className="w-2.5 h-2.5" />
                          <span>{field.label}</span>
                          {field.required && <span className="text-red-500">*</span>}
                        </div>
                      );
                    })}
                  </div>
                  
                  {design.enable_otp_verification && (
                    <div className="flex items-center justify-center gap-1 text-[9px] text-green-600">
                      <Shield className="w-2.5 h-2.5" />
                      <span>OTP Verification</span>
                    </div>
                  )}
                  
                  <button
                    className="mt-2 px-3 py-1.5 rounded-lg text-[10px] font-semibold w-full"
                    style={{ 
                      backgroundColor: design.vote_button_color,
                      color: design.secondary_color
                    }}
                  >
                    {design.vote_button_text}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Full Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vote Link Preview</DialogTitle>
          </DialogHeader>
          <div 
            className="rounded-lg overflow-hidden min-h-[500px]"
            style={{ 
              background: `linear-gradient(135deg, ${design.background_gradient_start} 0%, ${design.background_gradient_end} 100%)`
            }}
          >
            <div className="p-6">
              {/* Header */}
              <div className="text-center mb-6">
                {design.logo_url && (
                  <img src={getImageUrl(design.logo_url)} alt="Logo" className="h-12 mx-auto mb-4" />
                )}
                <h1 className="text-2xl font-bold text-white">{design.header_title}</h1>
                <p className="text-white/80 mt-2">{design.header_subtitle}</p>
              </div>

              {/* Banner */}
              {design.show_event_banner && (
                <div className="rounded-lg overflow-hidden mb-6">
                  {design.banner_url ? (
                    <img src={getImageUrl(design.banner_url)} alt="Banner" className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gray-700 flex items-center justify-center">
                      <span className="text-gray-400">Event Banner (1200x400 px)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Nominee Card */}
              <div 
                className="rounded-xl p-6 max-w-md mx-auto"
                style={{ backgroundColor: design.card_bg_color }}
              >
                {design.show_nominee_photo && (
                  <div 
                    className={`${photoSizeClasses[design.nominee_photo_size]} mx-auto rounded-full bg-gray-200 mb-4 flex items-center justify-center`}
                    style={{ border: `4px solid ${design.primary_color}` }}
                  >
                    <span className="text-gray-400 text-xs">Photo</span>
                  </div>
                )}
                <h2 className="text-xl font-bold text-center" style={{ color: design.text_color }}>
                  Sample Nominee Name
                </h2>
                {design.show_category && (
                  <p className="text-center mt-2" style={{ color: design.primary_color }}>
                    Excellence in Social Service Award
                  </p>
                )}
                {design.show_vote_count && (
                  <div className="text-center mt-2">
                    <span className="text-2xl font-bold" style={{ color: design.primary_color }}>1,234</span>
                    <span className="text-muted-foreground ml-1">votes</span>
                  </div>
                )}
                
                {/* Voter Form Preview */}
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-center" style={{ color: design.text_color }}>
                    Enter your details to vote
                  </p>
                  {design.voter_fields.filter(f => f.enabled).map((field) => {
                    const IconComponent = fieldIcons[field.icon] || User;
                    return (
                      <div key={field.id} className="relative">
                        <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type={field.type}
                          placeholder={`${field.label}${field.required ? ' *' : ''}`}
                          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                          disabled
                        />
                      </div>
                    );
                  })}
                  
                  {design.enable_otp_verification && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                      <div className="flex items-center justify-center gap-2 text-green-700 text-sm">
                        <Shield className="w-4 h-4" />
                        <span>OTP will be sent to your {design.otp_field}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <button
                  className="mt-4 px-6 py-3 rounded-lg font-semibold w-full"
                  style={{ 
                    backgroundColor: design.vote_button_color,
                    color: design.secondary_color
                  }}
                >
                  {design.enable_otp_verification ? 'Send OTP & Vote' : design.vote_button_text}
                </button>
                
                {design.show_share_button && (
                  <button className="mt-2 px-4 py-2 rounded-lg text-sm w-full border border-gray-200 text-gray-600 hover:bg-gray-50">
                    Share Voting Link
                  </button>
                )}
              </div>

              {/* Footer */}
              <p className="text-center text-white/60 mt-6 text-sm">{design.footer_text}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoteLinkDesigner;
